import { useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { CitationInput } from './citation-input';
import { ExternalLink, X } from 'lucide-react';
import type { Citation } from '@/lib/slack-styles';

interface AnswerEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  citations: Citation[];
  minHeight?: string;
}

/**
 * Text editor for golden answer with citation toolbar
 */
export function AnswerEditor({
  value,
  onChange,
  placeholder = 'Enter the correct answer here...',
  citations,
  minHeight = '200px',
}: AnswerEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleCitationInsert = (formatted: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange(value + ' ' + formatted);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = value.substring(0, start);
    const after = value.substring(end);

    // Insert at cursor position
    const newValue = before + formatted + after;
    onChange(newValue);

    // Move cursor after inserted citation
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formatted.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const removeCitation = (citation: Citation) => {
    // Find and remove the citation from the text
    const citationPattern = `[${citation.label}: ${citation.url}]`;
    const newValue = value.replace(citationPattern, '').replace(/\s{2,}/g, ' ').trim();
    onChange(newValue);
  };

  const wordCount = value.trim() ? value.trim().split(/\s+/).length : 0;
  const charCount = value.length;

  return (
    <div className="space-y-2">
      {/* Editor */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
        style={{ minHeight }}
      />

      {/* Toolbar - below textarea */}
      <div className="flex items-center justify-between">
        <CitationInput onInsert={handleCitationInsert} />
        <div className="text-xs text-muted-foreground">
          {wordCount} words • {charCount} characters
        </div>
      </div>

      {/* Active citations */}
      {citations.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Detected Citations ({citations.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {citations.map((citation, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="gap-1.5 pr-1"
              >
                <ExternalLink className="h-3 w-3" />
                <span className="max-w-[150px] truncate">{citation.label}</span>
                <button
                  type="button"
                  onClick={() => removeCitation(citation)}
                  className="ml-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AnswerEditor;
